import { applySnapshot, cast, flow, types } from 'mobx-state-tree';
import { NameModel } from '../base';
import { Appeals } from 'http-requests';
import { OthersStatusesModel } from './statuses';
import { ISelectValue } from 'components/ui';
import { IOtherEmployee, IOtherPerson, IOtherProvider } from '../../types';
import { IPersonFormData } from 'views/authorized/pages/appeals/tabs/create/components/steps/tabs/appealer/tabs/other-tab/components/modals/types';
import { killNulls, splitPhone } from 'utils';
import { IPhone } from 'types';
import { PhoneModel } from 'store/models/general-models';
import { AreaModel } from 'store/models/metersStore';

const BaseModel = NameModel.props({
  _id: types.optional(types.string, ''),
});

export const OtherPersonModel = BaseModel.props({
  phone_numbers: types.optional(types.array(PhoneModel), []),
  email: types.optional(types.maybeNull(types.string), null),
  first_name: types.optional(types.string, ''),
  last_name: types.optional(types.string, ''),
  patronymic_name: types.optional(types.maybeNull(types.string), null),
  short_name: types.optional(types.string, ''),
  full_name: types.optional(types.string, ''),
  position_name: types.optional(types.string, ''),
  other_provider_id: types.optional(types.string, ''),
})
  .actions((self) => ({
    addPhone: flow(function* (newPhone: string) {
      try {
        const updatedPerson = yield Appeals.UpdateOtherPerson(self._id, {
          ...self,
          phone_numbers: [
            { _type: 'cell', number: newPhone, add: null },
            ...self.phone_numbers,
          ],
        });

        applySnapshot(self, updatedPerson);
      } catch (error) {
        console.error(error);
      }
    }),
    setPhones(phones: IPhone[]) {
      self.phone_numbers = cast(phones);
    },
  }))
  .named('Person');

export const OtherEmployeeModel = BaseModel.props({
  last_name: types.optional(types.string, ''),
  first_name: types.optional(types.string, ''),
  patronymic_name: types.optional(types.maybeNull(types.string), null),
  short_name: types.optional(types.string, ''),
  full_name: types.optional(types.string, ''),
  phone_numbers: types.optional(types.array(PhoneModel), []),
  position_name: types.optional(types.string, ''),
  email: types.optional(types.maybeNull(types.string), null),
  other_provider_id: types.optional(types.string, ''),
})
  .actions((self) => ({
    addPhone: flow(function* (newPhone: string) {
      try {
        const updatedEmployee = yield Appeals.UpdateOtherEmployee(self._id, {
          ...self,
          phone_numbers: [
            { _type: 'cell', number: newPhone, add: null },
            ...self.phone_numbers,
          ],
        });

        applySnapshot(self, updatedEmployee);
      } catch (error) {
        console.error(error);
      }
    }),
    setPhones(phones: IPhone[]) {
      self.phone_numbers = cast(phones);
    },
  }))
  .named('Employee');

export const OtherMemberModel = types.model({
  area: types.optional(AreaModel, {}),
  email: types.optional(types.maybeNull(types.string), null),
  first_name: types.optional(types.string, ''),
  last_name: types.optional(types.string, ''),
  patronymic_name: types.optional(types.maybeNull(types.string), null),
  phones: types.optional(types.array(PhoneModel), []),
  id: types.optional(types.string, ''),
  short_name: types.optional(types.string, ''),
  str_name: types.optional(types.string, ''),
});

export const OtherProviderModel = BaseModel.props({
  name: types.optional(types.string, ''),
  inn: types.optional(types.maybeNull(types.string), ''),
  ogrn: types.optional(types.maybeNull(types.string), ''),
  provider_id: types.optional(types.string, ''),
});

export const OthersModel = types
  .model('Others', {
    statuses: types.optional(OthersStatusesModel, {}),
    persons: types.optional(types.array(OtherPersonModel), []),
    employees: types.optional(types.array(OtherEmployeeModel), []),
    providers: types.optional(types.array(OtherProviderModel), []),
  })
  .views((self) => ({
    get personsOptions(): ISelectValue[] {
      return self.persons.map((el) => ({
        value: el._id,
        label: el.fullName,
      }));
    },
    get employeesOptions(): ISelectValue[] {
      return self.employees.map((el) => ({
        value: el._id,
        label: el.fullName,
      }));
    },
    get providersOptions(): ISelectValue[] {
      return self.providers.map((el) => ({
        value: el._id,
        label: el.name,
      }));
    },
  }))
  .actions((self) => ({
    setPersonsList: (persons: Array<IOtherPerson>) => {
      self.persons.replace(persons);
    },
    setProvidersList: (providers: Array<IOtherProvider>) => {
      self.providers.replace(providers);
    },
    setEmployeesList: (employees: Array<IOtherEmployee>) => {
      self.employees.replace(employees);
    },
    addPerson: flow(function* (person: IPersonFormData & { phone: string }) {
      try {
        self.statuses.createPerson.loadingStates();

        const response = yield Appeals.CreateOtherPerson(
          killNulls({
            ...person,
            phone_numbers: [
              {
                _type: 'cell',
                number: person.phone,
              },
            ],
          })
        );

        self.persons.push(response);
        self.statuses.createPerson.successStates();

        return response;
      } catch (error) {
        self.statuses.createPerson.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.createPerson.finallyStates();
      }
    }),
    addProvider: flow(function* (params: { [key: string]: any }) {
      try {
        self.statuses.createProvider.loadingStates();

        const prepareParam: { [key: string]: any } = {
          name: params.service_name,
          ...(params.inn ? { inn: params.inn } : {}),
          ...(params.ogrn ? { ogrn: params.ogrn } : {}),
        };

        const newProvider = yield Appeals.CreateOtherProvider(prepareParam);

        self.providers.push(newProvider);
        self.employees.clear();
        self.statuses.createProvider.successStates();

        return newProvider;
      } catch (error) {
        self.statuses.createProvider.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.createProvider.finallyStates();
      }
    }),
    addEmployee: flow(function* (id: string, params: { [key: string]: any }) {
      try {
        self.statuses.createEmployee.loadingStates();

        const prepareParam: { [key: string]: any } = {
          last_name: params.last_name,
          first_name: params.first_name,
          patronymic_name: params.patronymic_name ?? null,
          email: params.email || null,
          phone_numbers: [
            {
              _type: 'cell',
              number: params.phone,
            },
          ],
          position_name: params.position,
          other_provider_id: id,
        };

        const newEmployee = yield Appeals.CreateOtherEmployee(prepareParam);

        self.employees.push(newEmployee);
        self.statuses.createEmployee.successStates();

        return newEmployee;
      } catch (error) {
        self.statuses.createEmployee.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.createEmployee.finallyStates();
      }
    }),
    getPersons: flow(function* (phone: string) {
      try {
        self.statuses.personsList.loadingStates();
        const response = yield Appeals.GetOtherPersons({ phone_number: phone });
        self.persons.replace(response);
        self.statuses.personsList.successStates();

        return self.persons;
      } catch (error) {
        self.statuses.personsList.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.personsList.finallyStates();
      }
    }),
    getProviders: flow(function* (strPhone: string) {
      try {
        self.statuses.providersList.loadingStates();

        const phone = splitPhone(strPhone);

        const response = yield Appeals.GetOtherProviders({
          phone_number: phone.code + phone.number,
        });

        self.providers.replace(response);
        self.statuses.providersList.successStates();

        return self.providers;
      } catch (error) {
        self.statuses.providersList.errorStates();
        console.error(error);
      } finally {
        self.statuses.providersList.finallyStates();
      }
    }),
    getEmployees: flow(function* (provider_id: string, phone_number: string) {
      try {
        self.statuses.employeesList.loadingStates();
        const response = yield Appeals.GetOtherEmployees({
          provider_id,
          phone_number,
        });
        self.employees.replace(response);
        self.statuses.employeesList.successStates();

        return self.employees;
      } catch (error) {
        self.statuses.employeesList.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.employeesList.finallyStates();
      }
    }),
  }));
